import React, { useState } from 'react';
import { AdminUserDetails } from '../../../../../types/user';
import { Card, Collapse, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import { Clear, FileDownload, FilterList, FilterListOff } from '@mui/icons-material';
import FilterTable, { Filter, FilterOption } from '../../../../FilterTable';
import { recordsToCsv } from '../../../../../utils/string';
import { DataGrid } from '@mui/x-data-grid';
import { UserTableRow } from '../../../../Table/UserRow';

interface UsersTableProps {
	usersData: AdminUserDetails[];
}

const UsersTable: React.FC<UsersTableProps> = ({ usersData }) => {
	const [filters, setFilters] = useState<Record<string, Filter>>({
		name: {
			enabled: false,
			value: '',
		},
		organisation: {
			enabled: false,
			value: '',
		},
		role: {
			enabled: false,
			value: '',
		},
		ispoc: {
			enabled: false,
			value: true,
		},
		touagreed: {
			enabled: false,
			value: true,
		},
	});
	const [filteredUsers, setFilteredUsers] = useState<AdminUserDetails[]>(usersData);
	const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

	// base set of roles extended to account for any new ones added in over time
	const orgRoleOptions: string[] = Array.from(
		new Set(
			['Investigator', 'OIC', 'Uploader', 'Supervisor', 'Manager'].concat(
				usersData.flatMap((user) => user.organisations.flatMap((org) => org.roles))
			)
		)
	);

	const getFilterValue = (filterType: string): string | boolean | undefined => {
		if (filterType in filters) {
			return filters[filterType].value;
		}
	};

	const updateUsers = (newFilters: typeof filters) => {
		let newUsers = [...usersData];
		if ('name' in newFilters && newFilters['name'].enabled) {
			newUsers = newUsers.filter((user) => user.name.toLowerCase().includes(String(newFilters['name'].value).toLocaleLowerCase()));
		}
		if ('organisation' in newFilters && newFilters['organisation'].enabled) {
			newUsers = newUsers.filter((user) =>
				user.organisations.some((org) =>
					org.name.toLocaleLowerCase().includes(String(newFilters['organisation'].value).toLocaleLowerCase())
				)
			);
		}
		if ('role' in newFilters && newFilters['role'].enabled && newFilters['role'].value) {
			newUsers = newUsers.filter((user) => user.organisations.some((org) => org.roles.includes(String(newFilters['role'].value))));
		}
		if ('ispoc' in newFilters && newFilters['ispoc'].enabled) {
			newUsers = newUsers.filter((user) => user.organisations.some((org) => org.pointOfContact === newFilters['ispoc'].value));
		}
		if ('touagreed' in newFilters && newFilters['touagreed'].enabled) {
			newUsers = newUsers.filter((user) => user.touAgreed === newFilters['touagreed'].value);
		}

		setFilteredUsers(newUsers);
	};

	// nb: this naively filters atm based on pre-fetched data
	// in future this may need to change to call the API whenever something is updated and lean on LogTo's search
	const updateFilters = (filterType: string, newState: string | boolean) => {
		const newFilters = {
			...filters,
			[filterType]: {
				enabled: true,
				value: newState,
			},
		};

		setFilters(newFilters);
		updateUsers(newFilters);
	};

	const resetFilters = () => {
		const newFilters = { ...filters };

		Object.keys(newFilters).forEach((k) => {
			newFilters[k].enabled = false;
			if (typeof newFilters[k].value === 'boolean') {
				newFilters[k].value = false;
			} else if (typeof newFilters[k].value === 'string') {
				newFilters[k].value = '';
			}
		});

		setFilters(newFilters);
		setFilteredUsers(usersData);
	};

	const downloadUsers = () => {
		const csv = recordsToCsv(
			filteredUsers.map((val) => {
				return {
					name: val.name,
					email: val.email,
					lastSignIn: new Date(val.lastSignIn).toISOString(),
				};
			}),
			['name', 'email', 'last_sign_in']
		);

		const csvBlob = new Blob([csv], { type: 'text/csv' });

		const downloadLink = document.createElement('a');
		downloadLink.href = window.URL.createObjectURL(csvBlob);
		downloadLink.setAttribute('download', `filtered-users-list-${new Date().getTime()}.csv`);

		document.body.appendChild(downloadLink);
		downloadLink.click();

		if (downloadLink.parentNode) {
			downloadLink.parentNode.removeChild(downloadLink);
		}
	};

	const filterOptions: FilterOption[] = [
		{
			label: 'Organisation',
			name: 'organisation',
			options: Array.from(new Set(Object.values(usersData).flatMap((userDetail) => userDetail.organisations.map((org) => org.name)))),
			componentType: 'autocomplete',
		},
		{
			label: 'Has Role',
			name: 'role',
			options: orgRoleOptions,
			componentType: 'autocomplete',
		},
		{
			label: 'Point of Contact',
			name: 'ispoc',
			options: ['Is a Point of Contact', 'Is not a Point of Contact'],
			componentType: 'boolean',
		},
		{
			label: 'Terms of Use',
			name: 'touagreed',
			options: ['Agreed', 'Not Yet Agreed'],
			componentType: 'boolean',
		},
	];

	return (
		<Stack gap={1}>
			<Card variant="outlined">
				<Stack direction="row" margin={1} gap={2}>
					<TextField
						fullWidth
						variant="outlined"
						label="Search by User Name"
						value={getFilterValue('name') || ''}
						onChange={(e) => {
							updateFilters('name', e.target.value);
						}}
						slotProps={{
							input: {
								endAdornment: (
									<IconButton
										onClick={() => updateFilters('name', '')}
										disabled={!getFilterValue('name')}
										data-testid={'clear-name-button'}
										aria-label="Clear Name Search Button"
									>
										<Clear />
									</IconButton>
								),
							},
						}}
						data-testid={'search-name-textfield'}
					/>
					<Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
						<IconButton onClick={() => setFiltersOpen(!filtersOpen)} data-testid={`toggle-filters-button`}>
							<FilterList />
						</IconButton>
					</Tooltip>
					<Tooltip title="Reset Filters">
						<IconButton onClick={() => resetFilters()} data-testid={'reset-filters-button'}>
							<FilterListOff />
						</IconButton>
					</Tooltip>
					<Tooltip title="Download Current User List as CSV">
						<IconButton onClick={() => downloadUsers()} data-testid={'download-users-button'} disabled={filteredUsers.length === 0}>
							<FileDownload />
						</IconButton>
					</Tooltip>
				</Stack>
				<Collapse in={filtersOpen}>
					<FilterTable filterOptions={filterOptions} filters={filters} setFilters={setFilters} updateData={updateUsers} />
				</Collapse>
			</Card>

			<DataGrid
				data-testid="Users-table"
				key={'usersTable'}
				rows={Array.from(filteredUsers)}
				columns={UserTableRow}
				initialState={{
					pagination: {
						paginationModel: { pageSize: 10, page: 0 },
					},
				}}
				disableColumnFilter
				sx={{
					'& .MuiDataGrid-row': {
						minHeight: '80px !important',
					},
				}}
				localeText={{
					noRowsLabel: 'No entries found',
				}}
				getRowHeight={() => 'auto'}
				getEstimatedRowHeight={() => 1000}
				autoHeight
				disableRowSelectionOnClick
				pageSizeOptions={[5, 10, 25, 50]}
				slotProps={{
					main: {
						'aria-label': 'Data Table',
					},
				}}
			/>
		</Stack>
	);
};

export default UsersTable;
